/**
 * numberUtils.js
 *
 * Shared number formatting utilities for consistent financial and metric display
 * Used across dashboards for currency, percentages, and large numbers
 */

/**
 * Format currency in Indian Rupees (₹)
 * @param {number} amount - Amount to format
 * @param {object} options - Formatting options
 *   @param {number} options.minimumFractionDigits - Minimum decimal places (default: 2)
 *   @param {number} options.maximumFractionDigits - Maximum decimal places (default: 2)
 *   @param {boolean} options.includeSymbol - Include ₹ symbol (default: true)
 *   @param {string} options.locale - Locale for formatting (default: 'en-IN')
 *   @param {boolean} options.removeTrailingZeros - Remove trailing zeros (default: true)
 * @returns {string} Formatted currency string
 *   Examples with removeTrailingZeros=true: "₹1,00,000", "₹50,000.5", "₹999.25"
 *   Examples with removeTrailingZeros=false: "₹1,00,000.00", "₹50,000.50", "₹999.25"
 */
export const formatCurrency = (amount, options = {}) => {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    includeSymbol = true,
    locale = 'en-IN',
    removeTrailingZeros = true
  } = options;

  if (amount === null || amount === undefined) return includeSymbol ? '₹0' : '0';

  try {
    // Format with commas using toLocaleString (e.g., "1,00,000.00")
    let formatted = parseFloat(amount).toLocaleString(locale, {
      minimumFractionDigits: removeTrailingZeros ? 0 : minimumFractionDigits,
      maximumFractionDigits
    });

    // If removeTrailingZeros is true, strip trailing zeros after decimal point only
    if (removeTrailingZeros) {
      // Remove trailing zeros that come after a decimal point, and remove empty decimal point
      formatted = formatted.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
    }

    return includeSymbol ? `₹${formatted}` : formatted;
  } catch (error) {
    console.error('Error formatting currency:', error);
    return includeSymbol ? '₹0' : '0';
  }
};

/**
 * Format currency dynamically - choose between K, L, and Cr based on amount
 * Automatically selects the most appropriate unit for the given amount
 * @param {number} amount - Amount to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted amount (e.g., "₹45.5K", "₹4.12L" or "₹30.41Cr")
 *
 * Rules:
 * - >= 10,000,000 (₹1 Cr) or <= -10,000,000: Format as Crores with 1-2 decimals
 * - >= 100,000 (₹1 L) or <= -100,000: Format as Lakhs with 1-2 decimals
 * - >= 1,000 (₹1 K) or <= -1,000: Format as Thousands with 1-2 decimals
 * - < 1,000: Format with thousand separators
 */
export const formatCurrencyDynamic = (amount, options = {}) => {
  const { decimals = 2 } = options;

  if (amount === null || amount === undefined) return '₹0';

  try {
    const absAmount = Math.abs(amount);

    // Amount >= ₹1 Crore (10 million): Format as Crores (handles both positive and negative)
    if (absAmount >= 10000000) {
      const croreValue = parseFloat((amount / 10000000).toFixed(decimals));
      return `₹${croreValue}Cr`;
    }
    // Amount >= ₹1 Lakh (100k): Format as Lakhs (handles both positive and negative)
    else if (absAmount >= 100000) {
      const lakhValue = parseFloat((amount / 100000).toFixed(decimals));
      return `₹${lakhValue}L`;
    }
    // Amount >= ₹1 Thousand (1k): Format as Thousands (handles both positive and negative)
    else if (absAmount >= 1000) {
      const thousandValue = parseFloat((amount / 1000).toFixed(decimals));
      return `₹${thousandValue}K`;
    }
    // Amount < ₹1 Thousand: Format with standard currency formatting
    else {
      return formatCurrency(amount);
    }
  } catch (error) {
    console.error('Error formatting currency dynamically:', error);
    return '₹0';
  }
};

/**
 * Format percentage
 * @param {number} value - Value to format (0-100)
 * @param {object} options - Formatting options
 * @returns {string} Formatted percentage (e.g., "85.50%")
 */
export const formatPercentage = (value, options = {}) => {
  const { decimals = 2, includeSymbol = true } = options;

  if (value === null || value === undefined) return includeSymbol ? '0%' : '0';

  try {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return includeSymbol ? '0%' : '0';

    // toFixed(decimals) formats the length, 
    // parseFloat() then removes any trailing zeros.
    const formatted = parseFloat(numValue.toFixed(decimals));
    
    return includeSymbol ? `${formatted}%` : formatted;
  } catch (error) {
    console.error('Error formatting percentage:', error);
    return includeSymbol ? '0%' : '0';
  }
};

/**
 * Format large numbers with thousand separators
 * @param {number} number - Number to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted number (e.g., "1,00,000")
 */
export const formatNumber = (number, options = {}) => {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale = 'en-IN'
  } = options;

  if (number === null || number === undefined) return '0';

  try {
    return parseFloat(number).toLocaleString(locale, {
      minimumFractionDigits,
      maximumFractionDigits
    });
  } catch (error) {
    console.error('Error formatting number:', error);
    return '0';
  }
};

/**
 * Format compact numbers (e.g., 1.2K, 1.5M)
 * Useful for displaying large numbers in limited space
 * @param {number} number - Number to format
 * @returns {string} Compact formatted number (e.g., "1.2K", "1.5M")
 */
export const formatCompactNumber = (number) => {
  if (number === null || number === undefined || number === 0) return '0';

  const units = ['', 'K', 'M', 'B', 'T'];
  let unitIndex = 0;
  let displayValue = Math.abs(number);

  while (displayValue >= 1000 && unitIndex < units.length - 1) {
    displayValue /= 1000;
    unitIndex++;
  }

  const decimals = displayValue < 10 ? 1 : 0;
  const formatted = displayValue.toFixed(decimals);
  const isNegative = number < 0 ? '-' : '';

  return `${isNegative}${formatted}${units[unitIndex]}`;
};

/**
 * Format time duration
 * @param {number} hours - Hours value
 * @param {object} options - Formatting options
 * @returns {string} Formatted duration (e.g., "2d 5h", "30m")
 */
export const formatDuration = (hours, options = {}) => {
  const { includeSeconds = false } = options;

  if (hours === null || hours === undefined) return '0h';

  try {
    const totalMinutes = Math.round(hours * 60);
    const days = Math.floor(totalMinutes / (24 * 60));
    const mins = totalMinutes % (24 * 60);
    const hrs = Math.floor(mins / 60);
    const minutes = mins % 60;

    if (days > 0) {
      return `${days}d ${hrs}h`;
    } else if (hrs > 0) {
      return `${hrs}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  } catch (error) {
    console.error('Error formatting duration:', error);
    return '0h';
  }
};

/**
 * Format labor / hours-spent values for tables and stats as hh:mm.
 * Uses an em dash for zero/empty by default. Totals may exceed 24h (e.g. "31:55").
 * @param {number|string|null|undefined} hours - Decimal hours from the API
 * @param {object} [options]
 * @param {string} [options.zeroDisplay='—'] - Shown when value is missing or <= 0
 * @returns {string} e.g. "2:00", "2:30", "31:55", "—"
 */
export const formatHoursSpent = (hours, options = {}) => {
  const { zeroDisplay = '—' } = options;

  if (hours === null || hours === undefined || hours === '') {
    return zeroDisplay;
  }

  const value = Number(hours);
  if (Number.isNaN(value)) return zeroDisplay;

  const totalMinutes = Math.round(value * 60);
  if (totalMinutes <= 0) return zeroDisplay;

  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hrs}:${String(mins).padStart(2, '0')}`;
};

/**
 * Calculate percentage change
 * @param {number} oldValue - Previous value
 * @param {number} newValue - Current value
 * @returns {object} Change information with sign and percentage
 */
export const calculatePercentageChange = (oldValue, newValue) => {
  if (oldValue === null || newValue === null || oldValue === 0) {
    return { percentage: 0, direction: 'neutral', symbol: '→', formatted: '→ 0%' };
  }

  try {
    const change = ((newValue - oldValue) / oldValue) * 100;
    const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';
    const symbol = change > 0 ? '↑' : change < 0 ? '↓' : '→';
    const formatted = `${symbol} ${Math.abs(change).toFixed(1)}%`;

    return {
      percentage: change,
      direction,
      symbol,
      formatted
    };
  } catch (error) {
    console.error('Error calculating percentage change:', error);
    return { percentage: 0, direction: 'neutral', symbol: '→', formatted: '→ 0%' };
  }
};

/**
 * Format ratio as percentage
 * @param {number} numerator - Numerator
 * @param {number} denominator - Denominator
 * @param {object} options - Formatting options
 * @returns {string} Formatted ratio (e.g., "75%")
 */
export const formatRatio = (numerator, denominator, options = {}) => {
  const { decimals = 1, includeSymbol = true } = options;

  if (!denominator || denominator === 0) return includeSymbol ? '0%' : '0';

  try {
    const ratio = (numerator / denominator) * 100;
    const formatted = ratio.toFixed(decimals);
    return includeSymbol ? `${formatted}%` : formatted;
  } catch (error) {
    console.error('Error formatting ratio:', error);
    return includeSymbol ? '0%' : '0';
  }
};

/**
 * Format count with contextual label
 * @param {number} count - Count value
 * @param {string} singular - Singular label
 * @param {string} plural - Plural label
 * @returns {string} Formatted count (e.g., "1 device", "5 devices")
 */
export const formatCount = (count, singular = 'item', plural = 'items') => {
  if (count === null || count === undefined) return `0 ${plural}`;

  try {
    return count === 1 ? `${count} ${singular}` : `${count} ${plural}`;
  } catch (error) {
    console.error('Error formatting count:', error);
    return `0 ${plural}`;
  }
};

/**
 * Cap nav/header badge counts for compact display (e.g. 99+)
 * @param {number} count
 * @param {number} [max=99]
 * @returns {string}
 */
export const formatBadgeCount = (count, max = 99) => {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n > max) return `${max}+`;
  return String(Math.floor(n));
};

/**
 * Format rating or score out of maximum
 * @param {number} score - Score value
 * @param {number} maxScore - Maximum score (default 5)
 * @param {boolean} includeStars - Include star emoji
 * @returns {string} Formatted score (e.g., "4.5/5 ⭐" or "4.5/5")
 */
export const formatRating = (score, maxScore = 5, includeStars = true) => {
  if (score === null || score === undefined) return includeStars ? '0/5 ⭐' : '0/5';

  try {
    const formatted = parseFloat(score).toFixed(1);
    return includeStars ? `${formatted}/${maxScore} ⭐` : `${formatted}/${maxScore}`;
  } catch (error) {
    console.error('Error formatting rating:', error);
    return includeStars ? '0/5 ⭐' : '0/5';
  }
};

/**
 * Format currency input in real-time with thousands separators (Indian locale)
 * Applied as user types to provide immediate visual feedback
 * @param {string} value - Raw input value from user
 * @returns {string} Formatted value with thousands separators (e.g., "10,00,000.50")
 *
 * Features:
 * - Removes all non-digit/non-decimal characters
 * - Limits to 2 decimal places
 * - Applies Indian locale formatting (10,00,000 not 1,000,000)
 * - Handles intermediate states (e.g., "100." when user is typing decimals)
 * - Returns empty string for empty input
 */
export const formatCurrencyInput = (value) => {
  // Allow empty value
  if (value === '') {
    return '';
  }

  // Remove all non-digit characters except decimal point
  let cleanValue = value.replace(/[^\d.]/g, '');

  // Handle multiple decimal points - keep only the first one and limit to 2 decimals
  if (cleanValue.includes('.')) {
    const parts = cleanValue.split('.');
    cleanValue = parts[0] + '.' + (parts[1] ? parts[1].substring(0, 2) : '');
  }

  // If only decimal point, return it to allow user to continue typing
  if (cleanValue === '.') {
    return '.';
  }

  // If empty after cleaning, return empty
  if (cleanValue === '') {
    return '';
  }

  // If ends with decimal (like "100."), return as-is to allow user to type decimals
  if (cleanValue.endsWith('.')) {
    return cleanValue;
  }

  // Parse the numeric value
  const numValue = parseFloat(cleanValue);
  if (isNaN(numValue)) {
    return cleanValue;
  }

  // If value is 0 or very small (like 0.01), return as-is without formatting
  if (numValue === 0) {
    return cleanValue;
  }

  // Split into integer and decimal parts to handle formatting correctly
  const hasDecimal = cleanValue.includes('.');
  let integerPart = cleanValue.split('.')[0];
  let decimalPart = cleanValue.includes('.') ? cleanValue.split('.')[1] : '';

  // Format only the integer part with thousands separators (Indian locale: 10,00,000)
  const integerValue = parseInt(integerPart || '0', 10);
  const formattedInteger = integerValue.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  // Combine integer and decimal parts
  if (hasDecimal) {
    return decimalPart ? `${formattedInteger}.${decimalPart}` : `${formattedInteger}.`;
  }

  return formattedInteger;
};

/**
 * Get raw numeric value from formatted currency string
 * Extracts just the numeric digits and decimal point, removing formatting characters
 * @param {string} formattedValue - Formatted currency string (e.g., "10,00,000.50")
 * @returns {string} Raw numeric value (e.g., "1000000.50")
 *
 * Used during form submission to extract the actual numeric value before sending to backend
 */
export const getCurrencyNumericValue = (formattedValue) => {
  if (!formattedValue) return '';
  // Remove all non-digit and non-decimal characters (removes commas and other formatting)
  return formattedValue.replace(/[^\d.]/g, '');
};
